/**
 * balance-structure-effects.ts — uniform query layer over the
 * v3M27 `StructureEffect[]` schema.
 *
 * Every consumer that wants to know "what does this structure do" goes
 * through these helpers. The effects array is the source of truth;
 * stack semantics (max-per-planet, sum-per-planet, sum-across-empire)
 * are baked into each effect variant and the helpers here apply them.
 *
 * Pure: takes state (or a planet / empire slice) and returns the
 * computed value. No side effects.
 */

import type {
  Empire,
  GameState,
  Planet,
  PlanetId,
  StructureEffect,
  StructureRow,
  StructuresData,
} from '../state/types';

// ---------------------------------------------------------------------------
// Effect lookup primitives
// ---------------------------------------------------------------------------

/** Type guard for the `multiplier` variant. */
export function isMultiplierEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'multiplier' }> {
  return effect.kind === 'multiplier';
}

/** Type guard for the `perTickIncome` variant. */
export function isPerTickIncomeEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'perTickIncome' }> {
  return effect.kind === 'perTickIncome';
}

/** Type guard for the `capBonus` variant. */
export function isCapBonusEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'capBonus' }> {
  return effect.kind === 'capBonus';
}

/** Type guard for the `bombardmentReduction` variant. */
export function isBombardmentReductionEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'bombardmentReduction' }> {
  return effect.kind === 'bombardmentReduction';
}

/** Type guard for the `scanRange` variant. */
export function isScanRangeEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'scanRange' }> {
  return effect.kind === 'scanRange';
}

/** Type guard for the `shipyardClassGate` variant. */
export function isShipyardClassGateEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'shipyardClassGate' }> {
  return effect.kind === 'shipyardClassGate';
}

/** Type guard for the `weaponEmitter` variant. */
export function isWeaponEmitterEffect(
  effect: StructureEffect,
): effect is Extract<StructureEffect, { kind: 'weaponEmitter' }> {
  return effect.kind === 'weaponEmitter';
}

// ---------------------------------------------------------------------------
// Per-planet queries (max / sum within a single planet)
// ---------------------------------------------------------------------------

/**
 * Multiplier for `resource` on `planetId`, taking the maximum across
 * all matching structures on the planet (per `maxPerPlanet` stack
 * semantics). Returns 1.0 (identity) when no matching structure
 * exists. Inactive structures (level === 0) are ignored.
 */
export function multiplierForPlanet(
  state: GameState,
  planetId: PlanetId,
  resource: Extract<StructureEffect, { kind: 'multiplier' }>['resource'],
): number {
  const planet = state.planets.get(planetId);
  if (!planet) return 1;
  let best = 1;
  for (const sid of planet.structureIds) {
    const s = state.structures.get(sid);
    if (!s || s.level < 1) continue;
    const row = state.structuresData.byKind[s.kind];
    if (!row) continue;
    for (const effect of row.effects) {
      if (!isMultiplierEffect(effect)) continue;
      if (effect.resource !== resource) continue;
      const value = effect.perLevel[s.level - 1] ?? 1;
      if (value > best) best = value;
    }
  }
  return best;
}

/**
 * Sum of `scanRange` contributions on `planetId` (`sumPerPlanet`
 * stack). Returns 0 when no matching structure exists.
 */
export function scanRangeBonusForPlanet(
  state: GameState,
  planetId: PlanetId,
): number {
  const planet = state.planets.get(planetId);
  if (!planet) return 0;
  let total = 0;
  for (const sid of planet.structureIds) {
    const s = state.structures.get(sid);
    if (!s || s.level < 1) continue;
    const row = state.structuresData.byKind[s.kind];
    if (!row) continue;
    for (const effect of row.effects) {
      if (!isScanRangeEffect(effect)) continue;
      total += effect.perLevel[s.level - 1] ?? 0;
    }
  }
  return total;
}

/**
 * Maximum bombardment-reduction fraction on `planetId` for a given
 * structure-kind filter (e.g. `'planetary-shields'` for the Shields
 * step in the damage chain, `'underground-bunkers'` for the Bunker
 * step). Returns 0 (no reduction) when no matching structure exists.
 *
 * The bombardment chain in `STRUCTURES.md §8.1` applies Shields and
 * Bunkers as separate multiplicative steps in a fixed order, so
 * consumers query each kind independently rather than asking for
 * "all bombardmentReduction effects on the planet."
 */
export function bombardmentReductionForKind(
  state: GameState,
  planetId: PlanetId,
  kindFilter: string,
): number {
  const planet = state.planets.get(planetId);
  if (!planet) return 0;
  let best = 0;
  for (const sid of planet.structureIds) {
    const s = state.structures.get(sid);
    if (!s || s.level < 1) continue;
    if (s.kind !== kindFilter) continue;
    const row = state.structuresData.byKind[s.kind];
    if (!row) continue;
    for (const effect of row.effects) {
      if (!isBombardmentReductionEffect(effect)) continue;
      const value = effect.perLevel[s.level - 1] ?? 0;
      if (value > best) best = value;
    }
  }
  return best;
}

/**
 * Highest ship-class buildable at `planetId` based on the best
 * `shipyardClassGate` effect (typically a single Shipyard at some
 * level). Returns 0 when no Shipyard is active — caller treats that
 * as "nothing can be built here."
 */
export function maxShipClassAtPlanet(
  state: GameState,
  planetId: PlanetId,
): number {
  const planet = state.planets.get(planetId);
  if (!planet) return 0;
  let best = 0;
  for (const sid of planet.structureIds) {
    const s = state.structures.get(sid);
    if (!s || s.level < 1) continue;
    const row = state.structuresData.byKind[s.kind];
    if (!row) continue;
    for (const effect of row.effects) {
      if (!isShipyardClassGateEffect(effect)) continue;
      const value = effect.perLevel[s.level - 1] ?? 0;
      if (value > best) best = value;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Per-empire queries (sum across all owned planets)
// ---------------------------------------------------------------------------

/**
 * Sum of `capBonus` contributions on `resource` across every
 * structure on every planet the empire owns. Stacks linearly per
 * `sumAcrossEmpire`.
 */
export function capBonusForEmpire(
  state: GameState,
  empire: Empire,
  resource: Extract<StructureEffect, { kind: 'capBonus' }>['resource'],
): number {
  let total = 0;
  for (const pid of empire.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    for (const sid of planet.structureIds) {
      const s = state.structures.get(sid);
      if (!s || s.level < 1) continue;
      const row = state.structuresData.byKind[s.kind];
      if (!row) continue;
      for (const effect of row.effects) {
        if (!isCapBonusEffect(effect)) continue;
        if (effect.resource !== resource) continue;
        total += effect.perLevel[s.level - 1] ?? 0;
      }
    }
  }
  return total;
}

/**
 * Sum of `perTickIncome` contributions on `resource` across every
 * structure on every planet the empire owns. Stacks linearly per
 * `sumAcrossEmpire`. Used by the Embassy-Diplomatic-fold path
 * (resource = `'diplomatic'`); could be used by future per-tick
 * resource emitters.
 */
export function perTickIncomeForEmpire(
  state: GameState,
  empire: Empire,
  resource: Extract<StructureEffect, { kind: 'perTickIncome' }>['resource'],
): number {
  let total = 0;
  for (const pid of empire.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    for (const sid of planet.structureIds) {
      const s = state.structures.get(sid);
      if (!s || s.level < 1) continue;
      const row = state.structuresData.byKind[s.kind];
      if (!row) continue;
      for (const effect of row.effects) {
        if (!isPerTickIncomeEffect(effect)) continue;
        if (effect.resource !== resource) continue;
        total += effect.perLevel[s.level - 1] ?? 0;
      }
    }
  }
  return total;
}

// ---------------------------------------------------------------------------
// Weapon-emitter slot enumeration
// ---------------------------------------------------------------------------

/** One armed weapon slot contributed by a structure on a planet. */
export interface WeaponEmitterSlot {
  /** The Structure entity that emits the slot. */
  structureId: number;
  /** Which weapon category the slot accepts. */
  category: 'energy' | 'kinetic' | 'concussion';
}

/**
 * v3M27 — flatten a planet's `weaponEmitter` effects into one
 * `WeaponEmitterSlot` per slot (multiple slots on one structure
 * per its level). The runtime resolves each slot's weapon via
 * `resolveSlotWeapon(empire.researchedTechs, slot.category)`.
 */
export function weaponEmitterSlotsForPlanet(
  state: GameState,
  planetId: PlanetId,
): WeaponEmitterSlot[] {
  const planet = state.planets.get(planetId);
  if (!planet) return [];
  const slots: WeaponEmitterSlot[] = [];
  for (const sid of planet.structureIds) {
    const s = state.structures.get(sid);
    if (!s || s.level < 1) continue;
    const row = state.structuresData.byKind[s.kind];
    if (!row) continue;
    for (const effect of row.effects) {
      if (!isWeaponEmitterEffect(effect)) continue;
      const slotCount = effect.slotCountPerLevel[s.level - 1] ?? 0;
      for (let i = 0; i < slotCount; i++) {
        slots.push({ structureId: s.id, category: effect.category });
      }
    }
  }
  return slots;
}

// ---------------------------------------------------------------------------
// Race-locked max-level lookup
// ---------------------------------------------------------------------------

/**
 * v3M27 — effective max level of `kind` for `raceId`. Reads
 * `maxLevelByRace[raceId]` if present, else falls back to `maxLevel`.
 * Returns 0 when the kind is unknown — caller treats that as "can't
 * build."
 */
export function maxLevelForRace(
  data: StructuresData,
  kind: string,
  raceId: string,
): number {
  const row = data.byKind[kind];
  if (!row) return 0;
  return row.maxLevelByRace[raceId] ?? row.maxLevel;
}

// ---------------------------------------------------------------------------
// Effect-array helpers (pure, testable in isolation)
// ---------------------------------------------------------------------------

/**
 * Return the first matching effect from a row's effects array, or
 * undefined if absent. Used by tests and authoring tools that want
 * to read a single effect by kind.
 */
export function findEffect<K extends StructureEffect['kind']>(
  row: StructureRow,
  kind: K,
): Extract<StructureEffect, { kind: K }> | undefined {
  return row.effects.find((e) => e.kind === kind) as
    | Extract<StructureEffect, { kind: K }>
    | undefined;
}

/**
 * Return all matching effects from a row's effects array. Used when
 * multiple of the same kind can coexist (e.g. a future structure
 * that boosts both Credits and Minerals via two separate
 * `multiplier` entries).
 */
export function findAllEffects<K extends StructureEffect['kind']>(
  row: StructureRow,
  kind: K,
): Array<Extract<StructureEffect, { kind: K }>> {
  return row.effects.filter((e) => e.kind === kind) as Array<
    Extract<StructureEffect, { kind: K }>
  >;
}

/** Re-export `Planet` so consumers don't need a separate state import. */
export type { Planet };
